/**
 * The password-reset token arrives in the link's fragment (#token=…), which
 * the browser never sends to a server. Links emailed before that change
 * carry it in the query string (?token=…) and are still read. Either way it
 * is taken out of the address before anything else runs (main.tsx calls this
 * before Sentry.init), so it is never in the URL that error reports, Session
 * Replay, the canonical tag or the browser history record. The page reads it
 * from here; it is kept in this tab's sessionStorage so a reload still works,
 * and cleared once the password is reset.
 */
const STORAGE_KEY = "rekinder:reset-token";
let memoryToken: string | null = null;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function sessionStore(): Store | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function takeResetTokenFromAddress(
  location: Pick<Location, "pathname" | "search" | "hash"> = window.location,
  history: Pick<History, "replaceState" | "state"> = window.history,
  store: Store | null = sessionStore(),
): void {
  if (!location.pathname.replace(/\/+$/, "").endsWith("/reset-password")) return;
  const query = new URLSearchParams(location.search);
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ""));
  const token = fragment.get("token") ?? query.get("token");
  if (token === null) return;
  memoryToken = token;
  try {
    store?.setItem(STORAGE_KEY, token);
  } catch {
    // Private mode: the in-memory copy still serves this page load.
  }
  query.delete("token");
  fragment.delete("token");
  const search = query.toString();
  const hash = fragment.toString();
  history.replaceState(history.state, "", `${location.pathname}${search ? `?${search}` : ""}${hash ? `#${hash}` : ""}`);
}

export function getResetToken(store: Store | null = sessionStore()): string {
  if (memoryToken) return memoryToken;
  try {
    return store?.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function clearResetToken(store: Store | null = sessionStore()): void {
  memoryToken = null;
  try {
    store?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
}
